/**
 * Step dependency graph for the 24-step business validation framework.
 *
 * Dependencies reflect natural handoff points between strategist and executor roles:
 * a strategist's output feeds directly into an executor's work (or vice versa).
 *
 * Used by:
 * - StepsList.tsx  — lock icon on blocked steps, notification dot on recently unblocked steps
 * - GenericStepWrapper.tsx — info banner when a step has unmet dependencies
 */

export interface StepDependency {
  /** The step that has a dependency (the "downstream" step) */
  stepNumber: number;
  /** Steps that must be completed (100%) before this step is unblocked */
  dependsOn: number[];
  /** Human-readable explanation of why this step depends on the upstream step */
  handoffMessage: string;
}

export const STEP_DEPENDENCIES: StepDependency[] = [
  // Step 3 (Executor: End User Profile) depends on Step 1 (Strategist: Market Segmentation)
  {
    stepNumber: 3,
    dependsOn: [1],
    handoffMessage:
      "Market Segmentation (Step 1) identifies which segments to focus on — this feeds directly into building the End User Profile.",
  },
  // Step 5 (Executor: Profile the Persona) depends on Step 2 (Strategist: Beachhead Market)
  {
    stepNumber: 5,
    dependsOn: [2],
    handoffMessage:
      "The Beachhead Market (Step 2) defines the specific segment to profile in depth for persona development.",
  },
  // Step 6 (Executor: Full Life Cycle Use Case) depends on Step 7 (Strategist: Product Specification)
  {
    stepNumber: 6,
    dependsOn: [7],
    handoffMessage:
      "The High-Level Product Specification (Step 7) describes what the product does — this is required before mapping the full life cycle use case.",
  },
  // Step 9 (Executor: Identify Next 10 Customers) depends on Step 8 (Strategist: Quantify Value Prop)
  {
    stepNumber: 9,
    dependsOn: [8],
    handoffMessage:
      "Quantifying the Value Proposition (Step 8) defines the specific benefit to offer — this is the pitch for identifying the next 10 customers.",
  },
  // Step 12 (Executor: DMU) depends on Step 11 (Strategist: Competitive Position)
  {
    stepNumber: 12,
    dependsOn: [11],
    handoffMessage:
      "The Competitive Position chart (Step 11) provides context on where the product stands — this informs the Decision-Making Unit analysis.",
  },
  // Step 18 (Executor: Sales Process) depends on Step 15 (Strategist: Business Model)
  {
    stepNumber: 18,
    dependsOn: [15],
    handoffMessage:
      "The Business Model (Step 15) defines how revenue is generated — this is the foundation for mapping the sales process.",
  },
  // Step 19 (Strategist: COCA) depends on Step 16 (Strategist: Pricing Framework)
  {
    stepNumber: 19,
    dependsOn: [16],
    handoffMessage:
      "The Pricing Framework (Step 16) sets the revenue baseline needed to calculate the Cost of Customer Acquisition.",
  },
  // Step 21 (Executor: Test Assumptions) depends on Step 22 (Strategist: Define the MVP)
  {
    stepNumber: 21,
    dependsOn: [22],
    handoffMessage:
      "The MVP Definition (Step 22) identifies what to build — this defines which assumptions need to be tested first.",
  },
  // Step 23 (Executor: Show Dogs Will Eat the Food) depends on Step 22 (Strategist: Define the MVP)
  {
    stepNumber: 23,
    dependsOn: [22],
    handoffMessage:
      "The MVP Definition (Step 22) is required to validate customer demand — you need to know what you're validating before running the test.",
  },
];

/**
 * Lookup: stepNumber → StepDependency (for the given step, what does it depend on?)
 */
export const stepDependencyMap: Record<number, StepDependency> = STEP_DEPENDENCIES.reduce(
  (acc, dep) => ({ ...acc, [dep.stepNumber]: dep }),
  {} as Record<number, StepDependency>
);

/**
 * Reverse lookup: completedStepNumber → array of dependent step numbers
 * (for a given step, which steps depend on it?)
 */
export const stepDependentsMap: Record<number, number[]> = STEP_DEPENDENCIES.reduce(
  (acc, dep) => {
    dep.dependsOn.forEach((parentStep) => {
      if (!acc[parentStep]) acc[parentStep] = [];
      acc[parentStep].push(dep.stepNumber);
    });
    return acc;
  },
  {} as Record<number, number[]>
);
